import { useEffect } from 'react';
import type { RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Stack of open dialogs (innermost last). Only the topmost trap reacts to
// Escape/Tab, so a modal opened from within a modal (Ticket→CFDIViewer,
// Store/Hour→LineParts) closes one layer at a time instead of all at once.
const trapStack: symbol[] = [];
// The body overflow to restore once the LAST dialog closes. Tracked at the stack
// level (not per-instance) so nested modals unmounting in any order can never
// leave the page scroll-locked.
let savedBodyOverflow = '';

/**
 * Modal/dialog focus management (WCAG 2.1.2 no-keyboard-trap-out, 2.4.3 focus
 * order, 4.1.2; Apple/Material modality). While mounted it:
 *  - remembers the element that had focus (the trigger),
 *  - moves focus into the dialog (first focusable, else the panel itself),
 *  - keeps Tab / Shift+Tab cycling WITHIN the dialog,
 *  - calls onClose on Escape,
 *  - locks body scroll,
 * and on unmount restores focus to the trigger and unlocks scroll.
 *
 * `ref` must point at the dialog panel. Render the dialog conditionally from the
 * parent (mount = open, unmount = closed), matching the app's existing pattern.
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    const id = Symbol('trap');
    trapStack.push(id);
    const node = ref.current;
    const prevActive = document.activeElement as HTMLElement | null;

    // Move focus into the dialog.
    const initial = node?.querySelector<HTMLElement>(FOCUSABLE);
    (initial ?? node)?.focus();

    function onKey(e: KeyboardEvent) {
      // Only the topmost open dialog handles keys.
      if (trapStack[trapStack.length - 1] !== id) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) {
        e.preventDefault();
        node.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === node)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKey, true);
    if (trapStack.length === 1) savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      const i = trapStack.lastIndexOf(id);
      if (i >= 0) trapStack.splice(i, 1);
      document.removeEventListener('keydown', onKey, true);
      if (trapStack.length === 0) document.body.style.overflow = savedBodyOverflow;
      // Restore focus to the trigger if it's still in the document.
      if (prevActive && document.contains(prevActive)) prevActive.focus();
    };
  }, [ref, onClose]);
}
